import React, { useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Row, Col, Button, Container, FormInput } from 'shards-react';
import AsyncSelect from 'react-select/async';
import PageTitle from '../../../components/PageTitle';
import Errors from '../../../components/Errors';
import { ReadinessReport } from '../../../components/common/ReadinessReport';
import { debouncedFetchAccounts } from '../../../utils/accounts';
import CredentialMigrationService from '../../../services/CredentialMigrationService';
import Translator from '../../../services/Translator';
import { Can } from '../../../permissions/helper';

const MIGRATE_CONFIRM_MESSAGE = '¿Confirmás que querés migrar la credencial a la cuenta destino?';

export function CheckReadiness() {
  const [credentialId, setCredentialId] = useState('');
  const [toAccount, setToAccount] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [migrationId, setMigrationId] = useState(null);
  const latestRequest = useRef(0);

  const trimmedCredentialId = credentialId.trim();
  const canCheck = trimmedCredentialId !== '' && toAccount !== null;
  const canMigrate =
    readiness !== null && readiness.readyToMigrate && readiness.canMigrateAsCredential !== false;

  const onCredentialIdChange = (e) => {
    latestRequest.current += 1;
    setCredentialId(e.target.value);
    setReadiness(null);
  };

  const onAccountChange = (option) => {
    latestRequest.current += 1;
    setToAccount(option);
    setReadiness(null);
  };

  const onCheck = () => {
    setErrors([]);
    setReadiness(null);
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .readiness(trimmedCredentialId, toAccount.value)
      .then((data) => {
        if (latestRequest.current !== requestId) return;
        if (!data) {
          setErrors(['No se encontró la credencial o la cuenta destino.']);
          return;
        }
        setReadiness(data);
      })
      .catch(() => {
        if (latestRequest.current === requestId) {
          setErrors(['No se pudo obtener el diagnóstico. Verificá el credentialId.']);
        }
      });
  };

  const onMigrate = () => {
    if (!window.confirm(MIGRATE_CONFIRM_MESSAGE)) return;

    setErrors([]);
    setSubmitting(true);
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .start(readiness.credentialId, readiness.toAccountId)
      .then((data) => {
        setSubmitting(false);
        if ((data.errors || []).length > 0) {
          setErrors(data.errors.map(Translator.migrationBlocker));
          return;
        }
        setMigrationId(data.migrationId);
      })
      .catch(() => {
        setSubmitting(false);
        setErrors(['No se pudo iniciar la migración.']);
      });
  };

  if (migrationId !== null) {
    return <Redirect to={`/admin/credentials/migrations/${migrationId}`} />;
  }

  return (
    <Container className="main-content-container px-4" fluid>
      <Row className="page-header py-4" noGutters>
        <PageTitle className="text-sm-left" sm="4" subtitle="" title="Verificar prerequisitos" />
      </Row>
      <Errors errors={errors} />
      <Row>
        <Col className="form-group" lg="4" sm="12" xs="12">
          <label htmlFor="credentialId">Credential ID</label>
          <FormInput
            id="credentialId"
            onChange={onCredentialIdChange}
            placeholder="Credential ID"
            value={credentialId}
          />
        </Col>
        <Col className="form-group" lg="4" sm="12" xs="12">
          <label htmlFor="toAccountId">Cuenta destino</label>
          <AsyncSelect
            cacheOptions={true}
            id="toAccountId"
            isClearable
            loadOptions={debouncedFetchAccounts}
            onChange={onAccountChange}
            placeholder="Busca la cuenta destino por nombre"
            value={toAccount}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Button disabled={!canCheck} onClick={onCheck}>
            Verificar
          </Button>
          {canMigrate && (
            <Can action="cred:edit:acc">
              <Button className="ml-2" disabled={submitting} onClick={onMigrate} theme="success">
                Migrar
              </Button>
            </Can>
          )}
        </Col>
      </Row>
      {readiness && (
        <Row className="MarginTop20">
          <Col>
            <ReadinessReport readiness={readiness} />
          </Col>
        </Row>
      )}
    </Container>
  );
}
